<script setup>
import { computed, inject } from 'vue';
import { styleVars } from './shared.js';

// A case-study spec sheet: label/value pairs in a ruled grid, with an
// optional photo beside them. 'dark' is the ink band (th-dark), 'plain'
// sits on the page. Drawn the same in Backyard, Hub and Groundwork
// through their redraw of th-dark, th-eyebrow and th-media.
const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// Empty: a prompt in the editor, nothing on the live site — the starter
// Projects page installs this block with its values blank, and a
// visitor must not read "Add specs in the editor." (ThSponsors does the
// same).
const inEditor = inject('tiqEditorCanvas', false);
const items = computed(() => (Array.isArray(props.config.items) ? props.config.items : [])
    .filter((it) => it && String(it.value ?? '').trim() !== ''));
const dark = computed(() => props.config.tone !== 'plain');
const cols = computed(() => (Number(props.config.columns) === 3 ? 3 : 2));
</script>

<template>
    <section v-if="items.length || inEditor" :id="config.anchor_id || null" class="th-block th-specs"
             :class="dark ? 'th-dark th-band' : 'th-sec'" :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.eyebrow || config.heading || config.body" class="th-specs__head">
                <p v-if="config.eyebrow" class="th-eyebrow">{{ config.eyebrow }}</p>
                <h2 v-if="config.heading" class="th-h2">{{ config.heading }}</h2>
                <p v-if="config.body" class="th-body th-specs__body">{{ config.body }}</p>
            </div>
            <div :class="config.image ? 'th-split th-specs__split' : null">
                <dl v-if="items.length" class="th-specs__grid" :style="{ '--th-specs-cols': cols }">
                    <div v-for="(it, i) in items" :key="i" class="th-specs__cell">
                        <dt class="th-specs__label">{{ it.label }}</dt>
                        <dd class="th-specs__value">{{ it.value }}</dd>
                    </div>
                </dl>
                <p v-else class="th-small th-specs__empty">Add specs in the editor.</p>
                <div v-if="config.image" class="th-media th-specs__photo" :class="{ 'th-media--dark': dark }">
                    <img :src="config.image" alt="">
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-specs__head { max-width: 60ch; margin-bottom: clamp(24px, 4vw, 36px); }
.th-specs__body { margin-top: 12px; }
.th-specs__split { align-items: start; }
.th-specs__grid {
    margin: 0;
    display: grid;
    grid-template-columns: repeat(var(--th-specs-cols, 2), minmax(0, 1fr));
    column-gap: 20px;
    border-top: 1px solid var(--th-line);
}
.th-dark .th-specs__grid, .th-dark .th-specs__cell { border-color: rgba(255, 255, 255, .16); }
.th-specs__cell { padding: 14px 0; border-bottom: 1px solid var(--th-line); min-width: 0; }
.th-specs__label { font: 500 11px/1.4 var(--th-mono); letter-spacing: .08em; text-transform: uppercase; color: var(--text-eyebrow, var(--th-meta)); }
.th-dark .th-specs__label { color: var(--text-eyebrow, rgba(255, 255, 255, .62)); }
.th-specs__value {
    margin: 4px 0 0;
    font: 700 22px/1.15 var(--th-display);
    color: var(--text-heading, inherit);
    overflow-wrap: anywhere;
}
.th-specs__photo { aspect-ratio: 4 / 3; }
.th-specs__empty { font-style: italic; }
@media (max-width: 520px) { .th-specs__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
</style>
